<template>
    <div :class="{ 'base-input--invalid' : invalid,
                   'base-input--has-prefix' : prefix,
                   'base-input--has-suffix' : suffix && !suffixIsIcon,
                   'base-input--has-suffix-icon' : suffixIsIcon,
                   'base-input--onfocus-hide-placeholder' : hidePlaceholderOnFocus,
                   'base-input--onfocus-hide-suffix' : hideSuffixOnFocus,
                   'base-input--hide-spin' : hideSpin,
                   'base-input--filter-style' : filterStyle,
                   'base-input--table-style' : tableStyle,
         }"
         class="base-input"
    >
        <span v-if="prefix"
              class="base-input__prefix"
        >
            <span v-if="!prefixIsIcon">
                {{ prefix }}
            </span>
        </span>

        <textarea :id="id"
                  ref="input"
                  v-bind="$attrs"
                  :type="$attrs.type || 'text'"
                  :aria-invalid="invalid"
                  :aria-describedby="`${id}_error`"
                  autocomplete="none"
                  class="base-input__input"
                  @input="(event) => {
                      $emit('update:value', event.target.value);
                      $emit('input', event.target.value);
                  }"
                  @keydown.enter="$event.stopPropagation()"
                  @blur="(event) => $emit('blur', event)"
                  v-on="inputListeners"
        />

        <label v-if="suffix"
               :for="id"
               :class="{ 'base-input__suffix--icon' : suffixIsIcon }"
               class="base-input__suffix"
        >
            <span v-if="!suffixIsIcon">
                {{ suffix }}
            </span>
            <svg v-if="suffix === 'icon-calendar'"
                 width="19"
                 height="19"
            >
                <use xlink:href="#calendar" />
            </svg>
            <svg v-if="suffix === 'icon-edit'"
                 width="19"
                 height="19"
            >
                <use xlink:href="#pencil" />
            </svg>
        </label>

        <i v-if="invalid"
           class="base-input__error-icon"
        />
    </div>
</template>

<script>
import '@/assets/icons/calendar.svg';
import '@/assets/icons/pencil.svg';

export default {
    name: 'BaseTextarea',
    props: {
        id: {
            type: String,
            required: true,
        },
        prefix: {
            type: String,
            default: undefined,
        },
        suffix: {
            type: String,
            default: undefined,
        },
        invalid: {
            type: Boolean,
            default: false,
        },
        filterStyle: {
            type: Boolean,
            default: false,
        },
        tableStyle: {
            type: Boolean,
            default: false,
        },
        hideSpin: {
            type: Boolean,
            default: false,
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        hidePlaceholderOnFocus: { type: Boolean, default: false },
        hideSuffixOnFocus: { type: Boolean, default: false },
    },
    data() {
        const inputListeners = {};
        Object.entries(this.$listeners).forEach(([name, cb]) => {
            if (name.startsWith('input-')) {
                const event = name.substr(6);
                inputListeners[event] = cb;
            }
        });
        return { inputListeners, innerValue: this.value };
    },
    computed: {
        prefixIsIcon() {
            return this.prefix && this.prefix.startsWith('icon');
        },
        suffixIsIcon() {
            return this.suffix && this.suffix.startsWith('icon');
        },
    },
    mounted() {
        if (this.autofocus) {
            this.$refs.input.focus();
        }
    },
};
</script>

<style scoped lang="scss">
::placeholder {
    @include input-placeholder-default;

    // goes on top of the box
    .base-input--filter-style & {
        @include input-placeholder-filter-style;
    }
}

.base-input {
    $_this: &;

    position: relative;

    &__prefix,
    &__suffix {
        position: absolute;
        top: 0;
        height: $input-height;
        color: $color-text-label;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: $letter-spacing-sm;
        line-height: $input-height + $input-border-width;
        text-align: center;
        text-transform: uppercase;
    }

    &__prefix {
        left: 0;
        padding-right: $prefix-padding-inner;
        padding-left: $prefix-padding-outer;
    }

    &__suffix {
        right: 0;
        padding-right: $prefix-padding-outer;
        padding-left: $prefix-padding-inner;

        &--icon {
            display: flex;
            align-items: center;
            color: $color-icon;
            cursor: pointer;

            svg {
                margin-top: -1px;
            }
        }
    }

    &__input {
        @include input-box-default;
        @include input-fonts-default;

        min-height: $input-textarea-height;

        &:disabled {
            @include input-box-disabled;

            ~ #{$_this}__suffix {
                &--icon {
                    svg {
                        color: $color-disabled-radio-check;
                    }
                }
            }
        }
    }

    // Set focus styles in a single place
    &__input:focus,
    &.focus #{$_this}__input,
    .focus & #{$_this}__input,
    &--invalid #{$_this}__input:focus,
    &--invalid.focus #{$_this}__input {
        @include input-box-focus;
    }

    &__error-icon {
        @include input-error-icon('on-input', $input-padding-wo-border + $prefix-padding-inner + $prefix-padding-outer);
    }

    &--has-prefix {
        #{$_this}__input {
            padding-left: $prefix-padding-inner + $prefix-padding-outer + $prefix-width;
        }
    }

    &--has-suffix {
        #{$_this}__input {
            padding-right: $prefix-padding-inner + $prefix-padding-outer + $suffix-width;
        }

        #{$_this}__error-icon {
            @include input-error-icon('on-input', $input-padding-wo-border + $suffix-width);
        }
    }

    &--has-suffix-icon {
        #{$_this}__input {
            padding-right: $prefix-padding-outer + $prefix-padding-inner + $icon-calendar-size;
        }

        #{$_this}__error-icon {
            @include input-error-icon('on-input',
            $input-padding-wo-border +
            $prefix-padding-inner +
            $icon-calendar-size);
        }
    }

    &--invalid {
        #{$_this}__input {
            @include input-box-invalid;
        }

        &#{$_this}--has-suffix {
            #{$_this}__input {
                padding-right:
                    $input-error-icon-size +
                    $prefix-padding-inner * 2 +
                    $suffix-width;
            }
        }

        &#{$_this}--has-suffix-icon {
            #{$_this}__input {
                padding-right:
                    $input-error-icon-size +
                    $prefix-padding-outer * 2 +
                    $icon-calendar-size;
            }
        }
    }

    &--filter-style {
        #{$_this}__input {
            @include input-box-filter-style;
            @include input-fonts-filter-style;
        }
    }

    &--table-style {
        #{$_this}__input {
            &:focus {
                box-shadow: none;
            }
        }

        &#{$_this}--invalid {
            #{$_this}__input,
            #{$_this}__input:focus {
                border-color: $color-error-bg-light;
                background-color: $color-error-bg-light;
                box-shadow: none;
            }
        }

        #{$_this}__error-icon {
            border-color: $color-error-dark;

            &::before,
            &::after {
                background-color: $color-error-dark;
            }
        }
    }

    &--onfocus-hide-placeholder {
        #{$_this}__input {
            &::placeholder {
                transition: $transition-quick;
                opacity: 1;
                color: $color-text-body;
            }

            &:focus {
                &::placeholder {
                    opacity: 0;
                }
            }
        }
    }

    &--onfocus-hide-suffix {
        #{$_this}__input {
            &:focus {
                + #{$_this}__suffix {
                    opacity: 0;
                }
            }
        }
    }

    &--hide-spin {
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
        }

        input[type=number] {
            -moz-appearance: textfield; /* Firefox */
        }
    }
}

@include ie11 {
    .base-input__input {
        padding-top: 0;
        padding-bottom: 0;
    }
}
</style>
